import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4StolenFromCarArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Items Stolen From Your Car? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Find out when renters insurance covers belongings stolen from your car, how off-premises theft limits work, and what auto insurance does not cover.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-items-stolen-from-car"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover items stolen from my car?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Yes — in many cases your HO-4 policy covers personal belongings stolen from your vehicle, but not the car
              itself or permanently installed parts. See also{' '}
              <Link to="/knowledge-base/does-ho4-cover-theft" style={articleLinkStyle}>general HO-4 theft coverage</Link>.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Renters insurance often covers <strong>personal property stolen from your car</strong> — laptops, bags,
                sports gear, and other belongings — under off-premises theft coverage. Your{' '}
                <strong>auto insurance comprehensive coverage</strong> handles the vehicle and built-in equipment, not
                loose items inside. Coverage depends on your policy limits, deductible, and specific terms.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Renters insurance vs. auto insurance for car break-ins</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>What was stolen or damaged</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Typical policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Laptop, purse, gym bag, clothing</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Renters insurance (HO-4)</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Aftermarket stereo, rims, car parts</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Auto comprehensive (if covered)</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Broken window or door lock from break-in</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Auto comprehensive</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Cash (usually)</td>
                      <td style={{ padding: '0.6rem' }}>Often excluded or very limited on both</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Off-premises theft limits to know</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Many HO-4 policies cover belongings away from home, but some apply a <strong>percentage cap</strong>{' '}
                  of your total personal property limit (commonly 10% when away from your residence).
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Your{' '}
                  <Link to="/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance" style={articleLinkStyle}>personal property limit</Link>{' '}
                  still applies — a $30,000 policy with a 10% off-premises cap may limit a single car theft claim to $3,000 unless your policy states otherwise.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  High-value items like jewelry or cameras may have separate sub-limits regardless of where the theft occurred.
                </li>
                <li>You pay your renters insurance deductible before the insurer pays the claim.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What to do after a car break-in</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>File a police report — insurers typically require one for theft claims.</li>
                <li style={{ marginBottom: '0.5rem' }}>Document everything stolen with photos, receipts, and serial numbers if available.</li>
                <li style={{ marginBottom: '0.5rem' }}>Contact your auto insurer for vehicle damage (window, lock, etc.).</li>
                <li style={{ marginBottom: '0.5rem' }}>File a renters insurance claim for stolen personal belongings.</li>
                <li>Review our{' '}
                  <Link to="/knowledge-base/how-to-file-a-renters-insurance-claim" style={articleLinkStyle}>renters insurance claims checklist</Link>{' '}
                  for documentation tips.
                </li>
              </ol>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>When coverage may not apply</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Items permanently installed in the vehicle (handled by auto policy).</li>
                <li style={{ marginBottom: '0.5rem' }}>Business equipment unless covered by a business policy or endorsement.</li>
                <li style={{ marginBottom: '0.5rem' }}>Items left in an unlocked vehicle, if your policy has negligence exclusions.</li>
                <li>Property belonging to a roommate not listed on your policy.</li>
              </ul>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage that protects your belongings at home and on the go.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Questions about off-premises theft limits?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can explain how your policy handles items stolen from a vehicle.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4StolenFromCarArticlePage;
